import { Col, FormGroup, Input, Label, Row, Card, CardBody, Badge, Button } from 'reactstrap';
import { SemanticModelLogicalTable } from '../../../../../../../api/initiative-data-integrations';
import { useState } from 'react';
import { validateTableName } from '../utils';

// Table Configuration Form Component
interface TableConfigFormProps {
  table: SemanticModelLogicalTable;
  schema: any;
  onTableChange: (table: SemanticModelLogicalTable) => void;
}

export const TableConfigForm: React.FC<TableConfigFormProps> = ({ table, schema, onTableChange }) => {
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const [showCustomColumnForm, setShowCustomColumnForm] = useState(false);
  const [editingCustomColumn, setEditingCustomColumn] = useState<any>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const handleTableChange = (field: keyof SemanticModelLogicalTable, value: any) => {
    onTableChange({ ...table, [field]: value });
  };

  // Get all available columns from the selected table in the schema (excluding Fivetran columns)
  const getAvailableColumns = () => {
    if (!schema || !table.base_table.schema || !table.base_table.table) {
      return [];
    }

    const schemaData = schema.schemas[table.base_table.schema];
    if (!schemaData?.tables?.[table.base_table.table]?.columns) {
      return [];
    }

    return Object.entries(schemaData.tables[table.base_table.table].columns)
      .filter(([columnName]) => !columnName.startsWith('_FIVETRAN_')) // Filter out Fivetran columns
      .map(([columnName, columnData]: [string, any]) => ({
        name: columnName,
        data_type: columnData.data_type || 'string',
        is_nullable: columnData.is_nullable || false,
        description: columnData.description || '',
      }));
  };

  // Get column configuration and category
  const getColumnInfo = (columnName: string) => {
    // Check each category to see where this column is configured
    // For standard database columns, we match by expr (the actual DB column name)
    const dimensionConfig = table.dimensions?.find((col) => col.expr === columnName);
    const timeDimensionConfig = table.time_dimensions?.find((col) => col.expr === columnName);
    const factConfig = table.facts?.find((col) => col.expr === columnName);

    if (dimensionConfig) return { category: 'dimensions' as const, config: dimensionConfig };
    if (timeDimensionConfig) return { category: 'time_dimensions' as const, config: timeDimensionConfig };
    if (factConfig) return { category: 'facts' as const, config: factConfig };

    return { category: null, config: null };
  };

  // Set column category (move column to the appropriate category)
  const setColumnCategory = (
    columnName: string,
    newCategory: 'dimensions' | 'time_dimensions' | 'facts' | null,
    columnData: any,
  ) => {
    // Remove from all categories first
    const updatedTable = {
      ...table,
      dimensions: table.dimensions?.filter((col) => col.expr !== columnName) || [],
      time_dimensions: table.time_dimensions?.filter((col) => col.expr !== columnName) || [],
      facts: table.facts?.filter((col) => col.expr !== columnName) || [],
    };

    // Add to new category if specified
    if (newCategory) {
      const newColumn: any = {
        name: columnName, // Start with database column name, but user can edit this
        expr: columnName, // Always the actual database column name for standard columns
        description: columnData.description || '',
        data_type: columnData.data_type || 'string',
        synonyms: [],
      };

      // Only add unique property for dimensions and time_dimensions
      if (newCategory === 'dimensions' || newCategory === 'time_dimensions') {
        newColumn.unique = false;
      }

      updatedTable[newCategory] = [...updatedTable[newCategory], newColumn];
    }

    onTableChange(updatedTable);
  };

  // Update column configuration
  const updateColumnConfig = (columnName: string, field: string, value: any) => {
    const { category } = getColumnInfo(columnName);
    if (!category) {
      return;
    }

    const currentColumns = [...(table[category] || [])];
    const columnIndex = currentColumns.findIndex((col) => col.expr === columnName);

    if (columnIndex >= 0) {
      // Only allow updating 'unique' for dimensions and time_dimensions
      if (field === 'unique' && category === 'facts') {
        return; // Don't update unique for facts
      }

      // Don't allow editing expr for standard database columns
      if (field === 'expr') {
        return; // expr should not be editable for standard columns
      }

      currentColumns[columnIndex] = { ...currentColumns[columnIndex], [field]: value };
      handleTableChange(category, currentColumns);
    }
  };

  const toggleColumnExpansion = (columnName: string) => {
    const newExpanded = new Set(expandedColumns);
    if (newExpanded.has(columnName)) {
      newExpanded.delete(columnName);
    } else {
      newExpanded.add(columnName);
    }
    setExpandedColumns(newExpanded);
  };

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case 'dimensions':
        return 'primary';
      case 'time_dimensions':
        return 'warning';
      case 'facts':
        return 'success';
      default:
        return 'light';
    }
  };

  const getCategoryIcon = (category: string | null) => {
    switch (category) {
      case 'dimensions':
        return 'fal fa-tag';
      case 'time_dimensions':
        return 'fal fa-clock';
      case 'facts':
        return 'fal fa-calculator';
      default:
        return 'fal fa-minus';
    }
  };

  // Check if a column is a custom column (not in the database schema)
  const isCustomColumn = (columnName: string) => {
    return !getAvailableColumns().some(col => col.name === columnName);
  };

  // Get all custom columns (configured columns that aren't in the database)
  const getCustomColumns = () => {
    const allConfiguredColumns: Array<{name: string, category: string, config: any}> = [];

    // Add dimensions
    table.dimensions?.forEach(col => {
      if (isCustomColumn(col.expr)) {
        allConfiguredColumns.push({
          name: col.expr,
          category: 'dimensions',
          config: col
        });
      }
    });

    // Add time dimensions
    table.time_dimensions?.forEach(col => {
      if (isCustomColumn(col.expr)) {
        allConfiguredColumns.push({
          name: col.expr,
          category: 'time_dimensions',
          config: col
        });
      }
    });

    // Add facts
    table.facts?.forEach(col => {
      if (isCustomColumn(col.expr)) {
        allConfiguredColumns.push({
          name: col.expr,
          category: 'facts',
          config: col
        });
      }
    });

    return allConfiguredColumns;
  };

  const handleAddCustomColumn = () => {
    setEditingCustomColumn({
      name: '',
      expr: '',
      category: 'dimensions',
      data_type: 'string',
      description: '',
      synonyms: [],
      unique: false
    });
    setShowCustomColumnForm(true);
    setShowAdvancedOptions(true); // Ensure advanced options are open
  };

  const handleEditCustomColumn = (customColumn: any) => {
    setEditingCustomColumn({
      name: customColumn.config.name,
      expr: customColumn.config.expr,
      category: customColumn.category,
      data_type: customColumn.config.data_type || 'string',
      description: customColumn.config.description || '',
      synonyms: customColumn.config.synonyms || [],
      unique: customColumn.config.unique || false
    });
    setShowCustomColumnForm(true);
    setShowAdvancedOptions(true); // Ensure advanced options are open
  };

  const handleSaveCustomColumn = () => {
    if (!editingCustomColumn?.name.trim() || !editingCustomColumn?.expr.trim()) {
      alert('Column name and SQL expression are required.');
      return;
    }

    // Remove existing custom column if editing
    const updatedTable = {
      ...table,
      dimensions: table.dimensions?.filter(col => col.expr !== editingCustomColumn.expr) || [],
      time_dimensions: table.time_dimensions?.filter(col => col.expr !== editingCustomColumn.expr) || [],
      facts: table.facts?.filter(col => col.expr !== editingCustomColumn.expr) || [],
    };

    // Create the new column object
    const newColumn: any = {
      name: editingCustomColumn.name,
      expr: editingCustomColumn.expr,
      description: editingCustomColumn.description,
      data_type: editingCustomColumn.data_type,
      synonyms: editingCustomColumn.synonyms,
    };

    // Only add unique property for dimensions and time_dimensions
    if (editingCustomColumn.category === 'dimensions' || editingCustomColumn.category === 'time_dimensions') {
      newColumn.unique = editingCustomColumn.unique;
    }

    // Add to the appropriate category
    if (editingCustomColumn.category === 'dimensions') {
      updatedTable.dimensions = [...(updatedTable.dimensions || []), newColumn];
    } else if (editingCustomColumn.category === 'time_dimensions') {
      updatedTable.time_dimensions = [...(updatedTable.time_dimensions || []), newColumn];
    } else if (editingCustomColumn.category === 'facts') {
      updatedTable.facts = [...(updatedTable.facts || []), newColumn];
    }

    onTableChange(updatedTable);
    setShowCustomColumnForm(false);
    setEditingCustomColumn(null);
  };

  const handleDeleteCustomColumn = (columnExpr: string) => {
    if (confirm('Are you sure you want to delete this custom column?')) {
      const updatedTable = {
        ...table,
        dimensions: table.dimensions?.filter(col => col.expr !== columnExpr) || [],
        time_dimensions: table.time_dimensions?.filter(col => col.expr !== columnExpr) || [],
        facts: table.facts?.filter(col => col.expr !== columnExpr) || [],
      };
      onTableChange(updatedTable);
    }
  };

  const handleCancelCustomColumn = () => {
    setShowCustomColumnForm(false);
    setEditingCustomColumn(null);
  };

  return (
    <div>
      {/* Basic Table Info */}
      <Card className='mb-4'>
        <CardBody>
          <h6 className='card-title mb-3'>
            <i className='fal fa-table me-2' />
            Table Information
          </h6>
          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label>Friendly name for this data table *</Label>
                <Input
                  type='text'
                  value={table.name}
                  onChange={(e) => handleTableChange('name', e.target.value)}
                  placeholder='e.g., orders, customer_data'
                  invalid={!!validateTableName(table.name)}
                />
                {validateTableName(table.name) && (
                  <div className='invalid-feedback d-block'>
                    {validateTableName(table.name)}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label>Short description for the AI</Label>
                <Input
                  type='text'
                  value={table.description || ''}
                  onChange={(e) => handleTableChange('description', e.target.value)}
                  placeholder='Brief description of this table'
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Column Configuration */}
      <div>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div>
            <h5 className='card-title mb-1'>
              <i className='fal fa-columns me-2' />
              Column Configuration
            </h5>
            <small className='text-muted'>
              Configure how each database column should be used in your semantic model
            </small>
          </div>
          <div className='d-flex gap-2'>
            <Badge color='primary' pill>
              <i className='fal fa-tag me-1' />
              Dimensions: {table.dimensions?.length || 0}
            </Badge>
            <Badge color='warning' pill>
              <i className='fal fa-clock me-1' />
              Time: {table.time_dimensions?.length || 0}
            </Badge>
            <Badge color='success' pill>
              <i className='fal fa-calculator me-1' />
              Facts: {table.facts?.length || 0}
            </Badge>
          </div>
        </div>

        {getAvailableColumns().length === 0 ? (
          <div className='text-center py-5 text-muted'>
            <i className='fal fa-table fa-3x mb-3' />
            <p>No columns available. Please ensure your table selection is correct.</p>
          </div>
        ) : (
          <div className='column-list'>
            {getAvailableColumns().map((availableColumn) => {
              const { category, config } = getColumnInfo(availableColumn.name);
              const isExpanded = expandedColumns.has(availableColumn.name);
              const isConfigured = !!config;

              return (
                <Card
                  key={availableColumn.name}
                  className={`mb-3 ${isConfigured ? `border-${getCategoryColor(category)}` : 'opacity-50'}`}
                >
                  <CardBody className='p-3'>
                    {/* Column Header */}
                    <Row className='align-items-center'>
                      <Col lg={4}>
                        <div className='d-flex align-items-center'>
                          <i className={`${getCategoryIcon(category)} me-2 text-${getCategoryColor(category)}`} />
                          <div className='d-flex flex-column'>
                            <div>
                              <strong className='text-dark'>{config?.name ? `${config.name} (${availableColumn.name})` : availableColumn.name}</strong>
                            </div>
                            <div>
                              <Badge color='secondary' className='me-2'>
                                {availableColumn.data_type}
                              </Badge>
                              {isConfigured && (
                                <Badge color={getCategoryColor(category)}>
                                  {category?.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </Badge>
                              )}
                            </div>
                            {availableColumn.description && (
                              <div className='text-muted mt-1'>{availableColumn.description}</div>
                            )}
                          </div>
                        </div>
                      </Col>

                      <Col lg={3}>
                        <FormGroup className='mb-0'>
                          <Label className='small fw-bold'>Category</Label>
                          <Input
                            type='select'
                            value={category || ''}
                            onChange={(e) =>
                              setColumnCategory(
                                availableColumn.name,
                                e.target.value ? (e.target.value as 'dimensions' | 'time_dimensions' | 'facts') : null,
                                availableColumn,
                              )
                            }
                            className='form-select'
                          >
                            <option value=''>Ignore</option>
                            <option value='dimensions'>Dimension</option>
                            <option value='time_dimensions'>Time Dimension</option>
                            <option value='facts'>Fact</option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col lg={3}>
                        {config && (
                          <FormGroup className='mb-0'>
                            <Label className='small fw-bold'>Data Type</Label>
                            <Input
                              type='select'
                              value={config.data_type || 'string'}
                              onChange={(e) => updateColumnConfig(availableColumn.name, 'data_type', e.target.value)}
                              className='form-select'
                            >
                              <option value='string'>String</option>
                              <option value='number'>Number</option>
                              <option value='boolean'>Boolean</option>
                              <option value='date'>Date</option>
                              <option value='timestamp'>Timestamp</option>
                            </Input>
                          </FormGroup>
                        )}
                      </Col>

                      <Col lg={2} className='text-end'>
                        {config && (
                          <Button
                            color='link'
                            size='sm'
                            onClick={() => toggleColumnExpansion(availableColumn.name)}
                            className='p-1'
                          >
                            <i className={`me-1 fal fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                            {isExpanded ? 'Less' : 'More'}
                          </Button>
                        )}
                      </Col>
                    </Row>

                    {/* Expanded Configuration */}
                    {config && isExpanded && (
                      <div className='mt-3 pt-3 border-top'>
                        <h6 className='card-title mb-1'>
                          <i className='fal fa-columns me-2' />
                          Help the AI understand your columns
                        </h6>
                        <Row>
                          <Col lg={4}>
                            <FormGroup>
                              <Label className='small fw-bold'>AI Label</Label>
                              <Input
                                type='text'
                                value={config.name}
                                onChange={(e) => updateColumnConfig(availableColumn.name, 'name', e.target.value)}
                                placeholder={availableColumn.name}
                              />
                            </FormGroup>
                          </Col>

                          <Col lg={4}>
                            <FormGroup>
                              <Label className='small fw-bold'>AI Description</Label>
                              <Input
                                type='text'
                                value={config.description || ''}
                                onChange={(e) =>
                                  updateColumnConfig(availableColumn.name, 'description', e.target.value)
                                }
                                placeholder='Describe how this column should be used'
                              />
                            </FormGroup>
                          </Col>

                          <Col lg={4}>
                            <FormGroup>
                              <Label className='small fw-bold'>AI Synonyms</Label>
                              <Input
                                type='text'
                                value={(config.synonyms || []).join(', ')}
                                onChange={(e) =>
                                  updateColumnConfig(
                                    availableColumn.name,
                                    'synonyms',
                                    e.target.value
                                      .split(',')
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  )
                                }
                                placeholder='Alternative names (comma-separated)'
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {(category === 'dimensions' || category === 'time_dimensions') && (
                          <Row>
                            <Col lg={12}>
                              <div className='form-check'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id={`unique-${availableColumn.name}`}
                                  checked={(config as any).unique || false}
                                  onChange={(e) => updateColumnConfig(availableColumn.name, 'unique', e.target.checked)}
                                />
                                <label className='form-check-label small' htmlFor={`unique-${availableColumn.name}`}>
                                  <strong>Unique values</strong> - This column contains unique values (like IDs)
                                </label>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}

        {/* Summary Footer */}
        {getAvailableColumns().length > 0 && (
          <div className='mt-4 p-3 bg-light rounded border'>
            <Row className='text-center'>
              <Col lg={3}>
                <div className='text-primary'>
                  <i className='fal fa-database fa-2x mb-2' />
                  <div className='fw-bold'>{getAvailableColumns().length}</div>
                  <small className='text-muted'>Total Columns</small>
                </div>
              </Col>
              <Col lg={3}>
                <div className='text-primary'>
                  <i className='fal fa-tag fa-2x mb-2' />
                  <div className='fw-bold'>{table.dimensions?.length || 0}</div>
                  <small className='text-muted'>Dimensions</small>
                </div>
              </Col>
              <Col lg={3}>
                <div className='text-warning'>
                  <i className='fal fa-clock fa-2x mb-2' />
                  <div className='fw-bold'>{table.time_dimensions?.length || 0}</div>
                  <small className='text-muted'>Time Dimensions</small>
                </div>
              </Col>
              <Col lg={3}>
                <div className='text-success'>
                  <i className='fal fa-calculator fa-2x mb-2' />
                  <div className='fw-bold'>{table.facts?.length || 0}</div>
                  <small className='text-muted'>Facts</small>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Advanced Options Toggle */}
        <div className='mt-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <Button
              color='link'
              className='p-0 text-start'
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            >
              <i className={`fal fa-chevron-${showAdvancedOptions ? 'down' : 'right'} me-2`} />
              Advanced Options
            </Button>
            {getCustomColumns().length > 0 && (
              <Badge color='info' className='ms-2'>
                {getCustomColumns().length} custom column{getCustomColumns().length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          {showAdvancedOptions && (
            <div className='advanced-options-content'>
              {/* Custom Columns Section */}
              <div className='border rounded p-3'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <div>
                    <h6 className='card-title mb-1'>
                      <i className='fal fa-code me-2' />
                      Custom Calculated Columns
                    </h6>
                    <small className='text-muted'>
                      Create custom columns using SQL expressions for advanced calculations
                    </small>
                  </div>
                  <Button color='primary' size='sm' onClick={handleAddCustomColumn}>
                    <i className='fal fa-plus me-1' />
                    Add Custom Column
                  </Button>
                </div>

                {getCustomColumns().length === 0 ? (
                  <div className='text-center py-4 text-muted border rounded'>
                    <i className='fal fa-code fa-2x mb-2' />
                    <p>No custom columns yet. Create calculated fields using SQL expressions.</p>
                    <small>Examples: UPPER(customer_name), revenue * (1 - discount), YEAR(created_date)</small>
                  </div>
                ) : (
                  <div className='custom-columns-list'>
                    {getCustomColumns().map((customColumn) => (
                      <Card key={customColumn.config.expr} className={`mb-2 border-${getCategoryColor(customColumn.category)}`}>
                        <CardBody className='p-3'>
                          <Row className='align-items-center'>
                            <Col lg={3}>
                              <div className='d-flex align-items-center'>
                                <i className={`${getCategoryIcon(customColumn.category)} me-2 text-${getCategoryColor(customColumn.category)}`} />
                                <div>
                                  <strong className='text-dark'>{customColumn.config.name}</strong>
                                  <div className='small text-muted'>
                                    <code>{customColumn.config.expr}</code>
                                  </div>
                                </div>
                              </div>
                            </Col>

                            <Col lg={2}>
                              <Badge color={getCategoryColor(customColumn.category)}>
                                {customColumn.category.replace('_', ' ')}
                              </Badge>
                            </Col>

                            <Col lg={2}>
                              <Badge color='secondary'>
                                {customColumn.config.data_type}
                              </Badge>
                            </Col>

                            <Col lg={3}>
                              <div className='small text-muted'>
                                {customColumn.config.description || 'No description'}
                              </div>
                            </Col>

                            <Col lg={2} className='text-end'>
                              <Button
                                color='primary'
                                size='sm'
                                outline
                                onClick={() => handleEditCustomColumn(customColumn)}
                                className='me-1'
                              >
                                <i className='fal fa-edit' />
                              </Button>
                              <Button
                                color='danger'
                                size='sm'
                                outline
                                onClick={() => handleDeleteCustomColumn(customColumn.config.expr)}
                              >
                                <i className='fal fa-trash' />
                              </Button>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Inline Custom Column Form */}
                {showCustomColumnForm && editingCustomColumn && (
                  <div className='mt-4 p-3 bg-light border rounded'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='mb-0'>
                        <i className='fal fa-code me-2' />
                        {editingCustomColumn.expr && editingCustomColumn.expr !== '' ? 'Edit Custom Column' : 'Add Custom Column'}
                      </h6>
                    </div>

                    <Row className='mb-3'>
                      <Col lg={6}>
                        <FormGroup>
                          <Label>AI-Friendly Name *</Label>
                          <Input
                            type='text'
                            value={editingCustomColumn.name}
                            onChange={(e) => setEditingCustomColumn({...editingCustomColumn, name: e.target.value})}
                            placeholder='e.g., Total Revenue, Customer Full Name'
                          />
                          <small className='text-muted'>How the AI should refer to this calculated column</small>
                        </FormGroup>
                      </Col>
                      <Col lg={6}>
                        <FormGroup>
                          <Label>Category *</Label>
                          <Input
                            type='select'
                            value={editingCustomColumn.category}
                            onChange={(e) => setEditingCustomColumn({...editingCustomColumn, category: e.target.value})}
                          >
                            <option value='dimensions'>Dimension</option>
                            <option value='time_dimensions'>Time Dimension</option>
                            <option value='facts'>Fact</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className='mb-3'>
                      <Col lg={12}>
                        <FormGroup>
                          <Label>SQL Expression *</Label>
                          <Input
                            type='textarea'
                            rows={3}
                            value={editingCustomColumn.expr}
                            onChange={(e) => setEditingCustomColumn({...editingCustomColumn, expr: e.target.value})}
                            placeholder='e.g., UPPER(customer_name), revenue * (1 - discount), YEAR(created_date)'
                          />
                          <small className='text-muted'>
                            Valid SQL expression. Use actual database column names.
                            <br />
                            Examples: <code>UPPER(first_name || ' ' || last_name)</code>, <code>amount * (1 - discount_rate)</code>
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className='mb-3'>
                      <Col lg={6}>
                        <FormGroup>
                          <Label>Data Type</Label>
                          <Input
                            type='select'
                            value={editingCustomColumn.data_type}
                            onChange={(e) => setEditingCustomColumn({...editingCustomColumn, data_type: e.target.value})}
                          >
                            <option value='string'>String</option>
                            <option value='number'>Number</option>
                            <option value='boolean'>Boolean</option>
                            <option value='date'>Date</option>
                            <option value='timestamp'>Timestamp</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col lg={6}>
                        {(editingCustomColumn.category === 'dimensions' || editingCustomColumn.category === 'time_dimensions') && (
                          <FormGroup>
                            <div className='form-check mt-4'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                id='unique-custom'
                                checked={editingCustomColumn.unique}
                                onChange={(e) => setEditingCustomColumn({...editingCustomColumn, unique: e.target.checked})}
                              />
                              <label className='form-check-label' htmlFor='unique-custom'>
                                Unique values
                              </label>
                            </div>
                          </FormGroup>
                        )}
                      </Col>
                    </Row>

                    <Row className='mb-3'>
                      <Col lg={6}>
                        <FormGroup>
                          <Label>Description</Label>
                          <Input
                            type='text'
                            value={editingCustomColumn.description}
                            onChange={(e) => setEditingCustomColumn({...editingCustomColumn, description: e.target.value})}
                            placeholder='Describe what this calculated column represents'
                          />
                        </FormGroup>
                      </Col>
                      <Col lg={6}>
                        <FormGroup>
                          <Label>Synonyms</Label>
                          <Input
                            type='text'
                            value={editingCustomColumn.synonyms.join(', ')}
                            onChange={(e) => setEditingCustomColumn({
                              ...editingCustomColumn,
                              synonyms: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean)
                            })}
                            placeholder='Alternative names (comma-separated)'
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className='d-flex justify-content-end gap-2'>
                      <Button color='primary' onClick={handleSaveCustomColumn}>
                        Save Custom Column
                      </Button>
                      <Button color='secondary' onClick={handleCancelCustomColumn}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
